import React, { useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import {
  LuAlignStartVertical,
  LuAlignCenterVertical,
  LuAlignEndVertical,
  LuAlignStartHorizontal,
  LuAlignEndHorizontal,
  LuBold,
  LuItalic,
  LuUnderline,
  LuAlignLeft,
  LuAlignCenter,
  LuAlignRight,
  LuAArrowUp,
  LuAArrowDown,
  LuSettings2,
} from "react-icons/lu";
import ColorPicker from "../../common/color-picker/ColorPicker";
import FontFamilyButton from "../editor_shortcuts/FontFamilyButton";
import "./_canvasOverlay.scss";
import "../editor_shortcuts/_assessmentMenu.scss";

/**
 * Floating alignment bar next to CanvasTopToolbar, shown while 2+ canvas
 * elements are selected. Left/right/top/bottom align to the outermost
 * element; center aligns to the middle element, so it stays disabled until
 * 3 are selected.
 *
 * When every selected element is a text box (BasicText/RichText), a second
 * group appears — the same quick actions (bold/italic/underline/align/font
 * size) and gear popover (font family/size/colors) as the single-box pill,
 * applied to ALL selected text boxes at once.
 */
const CanvasAlignToolbar = ({ selectedCount, onAlign, style, textFormat }) => {
  const [open, setOpen] = useState(false);
  if (selectedCount < 2) return null;

  const run = (type) => (e) => {
    onAlign(type);
    e.currentTarget.blur();
  };

  return (
    <div className="canvasOverlay canvasOverlay--topLeft" style={style}>
      <button
        type="button"
        className="canvasOverlayBtn"
        title={i18n.t("messages.align_left")}
        onClick={run("left")}
      >
        <LuAlignStartVertical size={19} />
      </button>
      <button
        type="button"
        className="canvasOverlayBtn"
        title={i18n.t("messages.align_center")}
        disabled={selectedCount < 3}
        onClick={run("center")}
      >
        <LuAlignCenterVertical size={19} />
      </button>
      <button
        type="button"
        className="canvasOverlayBtn"
        title={i18n.t("messages.align_right")}
        onClick={run("right")}
      >
        <LuAlignEndVertical size={19} />
      </button>
      <button
        type="button"
        className="canvasOverlayBtn"
        title={i18n.t("messages.align_up")}
        onClick={run("top")}
      >
        <LuAlignStartHorizontal size={19} />
      </button>
      <button
        type="button"
        className="canvasOverlayBtn"
        title={i18n.t("messages.align_down")}
        onClick={run("bottom")}
      >
        <LuAlignEndHorizontal size={19} />
      </button>

      {textFormat && (
        <>
          <div className="canvasOverlayDivider" />
          <FontFamilyButton
            label={i18n.t("BasicText.font_family")}
            value={textFormat.textFontFamily}
            onChange={textFormat.onSetFontFamily}
            onPreview={textFormat.onPreviewFontFamily}
            buttonClassName="canvasOverlayBtn"
          />
          <button
            type="button"
            className={"canvasOverlayBtn" + (textFormat.bold ? " is-active" : "")}
            title={i18n.t("BasicText.bold")}
            onClick={() => textFormat.onToggle("bold")}
          >
            <LuBold size={18} />
          </button>
          <button
            type="button"
            className={"canvasOverlayBtn" + (textFormat.italic ? " is-active" : "")}
            title={i18n.t("BasicText.italic")}
            onClick={() => textFormat.onToggle("italic")}
          >
            <LuItalic size={18} />
          </button>
          <button
            type="button"
            className={"canvasOverlayBtn" + (textFormat.underline ? " is-active" : "")}
            title={i18n.t("BasicText.underline")}
            onClick={() => textFormat.onToggle("underline")}
          >
            <LuUnderline size={18} />
          </button>
          <button
            type="button"
            className={"canvasOverlayBtn" + (textFormat.textAlign === "left" ? " is-active" : "")}
            title={i18n.t("messages.align_left")}
            onClick={() => textFormat.onSetAlign("left")}
          >
            <LuAlignLeft size={18} />
          </button>
          <button
            type="button"
            className={"canvasOverlayBtn" + (textFormat.textAlign === "center" ? " is-active" : "")}
            title={i18n.t("messages.align_center")}
            onClick={() => textFormat.onSetAlign("center")}
          >
            <LuAlignCenter size={18} />
          </button>
          <button
            type="button"
            className={"canvasOverlayBtn" + (textFormat.textAlign === "right" ? " is-active" : "")}
            title={i18n.t("messages.align_right")}
            onClick={() => textFormat.onSetAlign("right")}
          >
            <LuAlignRight size={18} />
          </button>
          <button
            type="button"
            className="canvasOverlayBtn"
            title={i18n.t("messages.increase_text_size")}
            onClick={() => textFormat.onFontScale(1)}
          >
            <LuAArrowUp size={18} />
          </button>
          <button
            type="button"
            className="canvasOverlayBtn"
            title={i18n.t("messages.decrease_text_size")}
            onClick={() => textFormat.onFontScale(-1)}
          >
            <LuAArrowDown size={18} />
          </button>
          <button
            type="button"
            className={"canvasOverlayBtn" + (open ? " is-active" : "")}
            title={i18n.t("AssessmentMenu.moreOptions")}
            aria-haspopup="true"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            <LuSettings2 size={18} />
          </button>

          {open && (
            <div
              className="assessmentBoxMenu am-popover"
              role="group"
              aria-label={i18n.t("AssessmentMenu.moreOptions")}
              style={{ position: "absolute", top: "100%", left: 0, marginTop: 8 }}
            >
              <div className="am-row">
                <span className="am-lbl">{i18n.t("BasicText.font_size")}</span>
                <span className="am-stepper">
                  <button
                    type="button"
                    aria-label="-"
                    onClick={() =>
                      textFormat.onSetFontSize(Math.max(6, (textFormat.textFontSize || 16) - 2))
                    }
                  >
                    −
                  </button>
                  <b>{textFormat.textFontSize || 16}</b>
                  <button
                    type="button"
                    aria-label="+"
                    onClick={() =>
                      textFormat.onSetFontSize(Math.min(200, (textFormat.textFontSize || 16) + 2))
                    }
                  >
                    +
                  </button>
                </span>
              </div>
              <div className="am-row am-stack">
                <span className="am-lbl">{i18n.t("BasicText.text_color")}</span>
                <ColorPicker
                  value={textFormat.textColor || "#000000"}
                  onChange={(next) => textFormat.onSetColor(next.color)}
                />
              </div>
              <div className="am-row am-stack">
                <span className="am-lbl">{i18n.t("BasicText.highlight_color")}</span>
                <ColorPicker
                  value={textFormat.highlightColor || "rgba(255,255,255,0)"}
                  onChange={(next) => textFormat.onSetHighlight(next.color)}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

CanvasAlignToolbar.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  onAlign: PropTypes.func.isRequired,
  style: PropTypes.object,
  // Present (non-null) only when every selected box is a text plugin
  // (BasicText/RichText). Mirrors the single-box pill's text controls,
  // applied to every selected text box at once.
  textFormat: PropTypes.shape({
    bold: PropTypes.bool,
    italic: PropTypes.bool,
    underline: PropTypes.bool,
    textAlign: PropTypes.string,
    textFontFamily: PropTypes.string,
    textFontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    textColor: PropTypes.string,
    highlightColor: PropTypes.string,
    onToggle: PropTypes.func,
    onSetAlign: PropTypes.func,
    onFontScale: PropTypes.func,
    onPreviewFontFamily: PropTypes.func,
    onSetFontFamily: PropTypes.func,
    onSetFontSize: PropTypes.func,
    onSetColor: PropTypes.func,
    onSetHighlight: PropTypes.func,
  }),
};

export default CanvasAlignToolbar;
